"use client";

import {Box, Flex, Stack, Text, TextProps} from "@chakra-ui/react";
import screenshotSlack from "../../images/screenshots/slack.png";
import screenshotSingleSourceTruth from "../../images/screenshots/singlesourcetruth.png";
import ScreenshotSync from "./images/sync.png";
import {Container} from "../../components/Container";
import Heading from "../../components/Heading";
import {useEffect, useState} from "react";
import NextImage from "../../components/Image";
import {StaticImageData} from "next/image";

const Strong = ({children}: TextProps) => (
  <Text as="strong" fontWeight="700" display="inline">
    {children}
  </Text>
);

type FeatureT = {
  title: string;
  description: React.ReactNode;
  image: StaticImageData;
};

const features: FeatureT[] = [
  {
    title: "Use every piece of data",
    description: (
      <>
        Graph pulls information from across your business, including <Strong>structured information</Strong> from your
        CRM and GRC tooling and <Strong>unstructured information</Strong> like PDF documents and entries from your wiki.
      </>
    ),
    image: ScreenshotSync,
  },
  {
    title: "Automatically identify conflicting & stale information",
    description: (
      <>
        Graph constantly analyses information, identifying stale or conflicting data and surfacing it for automatic or
        manual remediation.
      </>
    ),
    image: screenshotSingleSourceTruth,
  },
  {
    title: "Information organised across your business",
    description: (
      <>
        Graph automatically organises information across multiple product lines, configurations, business units and
        legal entities. Then, with powerful search, segmentation and clustering, your team can always find what they
        need.
      </>
    ),
    image: screenshotSlack,
  },
];

const Mobile = () => (
  <Stack color="white" flex="2" gap={24} mt={24}>
    {features.map(({title, description, image}) => (
      <Stack key={title} gap={12}>
        <Stack as={Stack} align="flex-start" flex={1} gap={4}>
          <Heading size="lg" fontWeight="600" w="full" letterSpacing="normal" lineHeight="normal">
            {title}
          </Heading>
          <Text fontSize="md">{description}</Text>
        </Stack>
        <Box position="relative" mx="auto" w="full" rounded="lg" bg="white" shadow="xl">
          <NextImage src={image} rounded={["md", "md", "xl"]} shadow="2xl" minW={["500px", "0px"]} />
        </Box>
      </Stack>
    ))}
  </Stack>
);

const Desktop = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [tab, setTab] = useState<FeatureT | null>(null);

  useEffect(() => {
    const t = setInterval(() => setTabIndex((tabIndex + 1) % features.length), 10000);
    return () => clearInterval(t);
  }, [setTabIndex, tabIndex]);

  const value = tab ? tab : features[tabIndex];

  return (
    <Flex mt={20} py="5.3rem">
      <Stack color="white" gap="3px" flex="2" minW="lg" mt={-4}>
        {features.map(f => (
          <Stack
            key={f.title}
            onClick={() => setTab(f)}
            align="flex-start"
            roundedLeft="2xl"
            p={6}
            pr={8}
            border="3px solid"
            borderRight="none"
            cursor="pointer"
            _hover={{
              bg: "rgba(255,255,255,.05)",
            }}
            bg={f === value ? "rgba(255,255,255,.1)" : ""}
            borderColor={f === value ? "rgba(255,255,255,.1)" : "transparent"}
          >
            <Heading size="md" fontWeight="700" w="full" letterSpacing="normal">
              {f.title}
            </Heading>
            <Text fontSize="md">{f.description}</Text>
          </Stack>
        ))}
      </Stack>
      <Box flex="3">
        <Box p={0} position="relative">
          <NextImage src={value.image} minW="60rem" position="absolute" rounded="xl" top="-5.4rem" shadow="2xl" />
        </Box>
      </Box>
    </Flex>
  );
};

const PrimaryFeatures = () => (
  <Box
    as="section"
    id="automatic-maintenance"
    aria-label="Features for running your books"
    bg="green.800"
    pb={[28, 32]}
    pt={[20, 32]}
    bgGradient="graph"
    overflow="hidden"
  >
    <Container position="relative">
      <Box
        maxW={["2xl", "2xl", "2xl", "2xl", "none"]}
        mx={[null, null, "auto"]}
        textAlign={[null, null, "center"]}
        className="max-w-2xl md:mx-auto md:text-center xl:max-w-none"
      >
        <Heading size={["2xl", "3xl"]} color="white">
          A knowledgebase that maintains itself
        </Heading>
        <Text mt={6} fontSize="md" color="white" maxW="xl" mx="auto">
          Platformed Graph evolves as your business evolves.
        </Text>
      </Box>

      <Box hideBelow="lg">
        <Desktop />
      </Box>

      <Box hideFrom="lg">
        <Mobile />
      </Box>
    </Container>
  </Box>
);

export default PrimaryFeatures;
